import { Check, Hexagon, Mic, MicOff, Repeat, Share2, Shield, Sparkles, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatDateTime, formatDuration } from './format';
import { scoreSessionCompatibility } from './matching';
import { Link } from './router';
import { renderIcon } from './icons';
import { useApp } from './store';
import type { PlayerProfile, SessionStatus, SessionSummary } from './types';

export function Logo() {
  return (
    <span className="logo" aria-label="TeamCraft">
      <Hexagon size={20} strokeWidth={2.4} />
      <span>TeamCraft</span>
    </span>
  );
}

export function HexAvatar({ label, size = 'md', accent, icon }: { label: string; size?: 'sm' | 'md' | 'lg'; accent?: string | null; icon?: string | null }) {
  const iconSize = size === 'lg' ? 28 : size === 'sm' ? 14 : 20;
  const rendered = renderIcon(icon, iconSize);
  return (
    <span className={`hex-avatar ${size}`} style={accent ? { color: accent, borderColor: accent } : undefined} aria-hidden="true">
      {rendered ?? label}
    </span>
  );
}

export function LevelMark({ level, title }: { level: number; title: string }) {
  return (
    <span className="level-mark" title={`Level ${level} · ${title}`}>
      <span className="level-hex">{level}</span>
      <span className="level-title">{title}</span>
    </span>
  );
}

const STATUS_LABELS: Record<SessionStatus, string> = {
  open: 'Open',
  full: 'Full',
  in_progress: 'In progress',
  completed: 'Completed',
  canceled: 'Canceled'
};

export function StatusChip({ status }: { status: SessionStatus }) {
  return <span className={`status-chip status-${status}`}>{STATUS_LABELS[status]}</span>;
}

export function FitBadge({ session, profile }: { session: SessionSummary; profile: PlayerProfile }) {
  const fit = scoreSessionCompatibility(profile, session);
  return (
    <span className="fit-badge" title={fit.reasons.join(' · ')}>
      <Sparkles size={13} /> {fit.score} · {fit.level}
    </span>
  );
}

export function SessionCard({ session, profile, featured = false }: { session: SessionSummary; profile: PlayerProfile | null; featured?: boolean }) {
  const fit = profile ? scoreSessionCompatibility(profile, session) : null;
  return (
    <Link to={`/sessions/${session.id}`} className={`session-card ${featured ? 'featured' : ''}`} aria-label={`Open session ${session.title}`}>
      <div className="card-topline">
        <span className="game-tag" style={session.gameAccent ? { color: session.gameAccent } : undefined}>
          {session.gameName} · {session.activity}
        </span>
        <StatusChip status={session.status} />
      </div>
      <h3>{session.title}</h3>
      {(session.guild || session.repeatsWeekly) && (
        <div className="card-badges">
          {session.guild && <span className="pill mint"><Shield size={11} /> {session.guild.tag ?? session.guild.name}</span>}
          {session.repeatsWeekly && <span className="pill"><Repeat size={11} /> Weekly</span>}
        </div>
      )}
      <div className="session-meta">
        <span className="mono">{formatDateTime(session.startTime)}</span>
        <span className="mono">{formatDuration(session.durationMinutes)}</span>
        <span><Users size={13} />{session.currentPlayers}/{session.partySize}{session.waitlistCount > 0 ? ` +${session.waitlistCount}` : ''}</span>
        <span>{session.voice === 'none' ? <MicOff size={13} /> : <Mic size={13} />}{session.voice === 'required' ? 'Voice' : session.voice === 'none' ? 'No voice' : 'Voice opt.'}</span>
        <span>{session.platform}</span>
      </div>
      {session.requiredRoles.length > 0 && (
        <div className="roles">{session.requiredRoles.map((role) => <span key={role}>{role}</span>)}</div>
      )}
      <div className="session-footer">
        <span className="host-line">
          <HexAvatar label={session.hostAvatar} size="sm" icon={session.hostAvatarIcon} accent={session.hostAvatarColor} />
          {session.hostUsername}
        </span>
        <span className="session-footer-actions">
          {fit && <FitBadge session={session} profile={profile!} />}
          <ShareButton url={`/sessions/${session.id}`} title={session.title} text={`Join this ${session.gameName} session on TeamCraft`} label="" variant="icon-share" />
        </span>
      </div>
      {fit && fit.reasons.length > 0 && (
        <div className="reasons">{fit.reasons.slice(0, 3).map((reason) => <span key={reason}><Check size={11} />{reason}</span>)}</div>
      )}
    </Link>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint: string; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <span className="empty-hex" aria-hidden="true"><Hexagon size={28} /></span>
      <strong>{title}</strong>
      <p>{hint}</p>
      {action}
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function ChipSelect({ options, selected, onToggle, single = false }: {
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onToggle: (value: string) => void;
  single?: boolean;
}) {
  return (
    <div className="chip-select" role={single ? 'radiogroup' : 'group'}>
      {options.map((option) => {
        const active = selected.includes(option.value);
        return (
          <button
            type="button"
            key={option.value}
            className={`chip ${active ? 'active' : ''}`}
            aria-pressed={active}
            onClick={() => onToggle(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function ProgressBar({ value }: { value: number }) {
  return (
    <div className="progress-track" role="progressbar" aria-valuenow={Math.round(value * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div className="progress-fill" style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }} />
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <div className="loading" role="status">{label}</div>;
}

// One-tap share: native share sheet on mobile, copy-to-clipboard on desktop.
export function ShareButton({ url, title, text, label = 'Share', variant = 'ghost small', copyMessage = 'Link copied — paste it anywhere to invite your squad.' }: {
  url: string;
  title: string;
  text?: string;
  label?: string;
  variant?: string;
  copyMessage?: string;
}) {
  const { toast } = useApp();
  async function share(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text, url: absolute });
      } catch {
        // User dismissed the share sheet.
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(absolute);
      toast(copyMessage, 'success');
    } catch {
      toast('Could not copy the link.', 'error');
    }
  }
  return (
    <button className={variant} onClick={share} aria-label={`Share ${title}`} title={label || 'Share'}>
      <Share2 size={14} />{label ? ` ${label}` : ''}
    </button>
  );
}
